import type { JSX } from 'react';

/**
 * The adventurer — a student's RPG character.
 *
 * Drawn the way `Mascot` is drawn: hand-authored inline SVG, no image assets,
 * no animation library. Anything that moves does so by class, so
 * `prefers-reduced-motion` keeps being honoured by the one global rule in
 * `theme.css`. Same props in, same SVG out — `packages/ui` sits below
 * `apps/web` and the no-randomness rule applies here exactly as it does to
 * `Confetti`.
 *
 * The option unions mirror `AvatarConfig` in `@yuan-math/learning`
 * structurally rather than importing it: this package depends on nothing
 * above it, and a drawing should not need to know what a learner is.
 *
 * The class is a costume (docs/rpg.md §10) — each one is a silhouette and a
 * prop, never a capability. Decorative by default: the drawing is `aria-hidden`
 * and the page supplies the accessible name beside it, which is right wherever
 * the name is already written next to the picture. A page where the drawing is
 * the subject passes `label` and gets an image with a name instead.
 */
export type AvatarSkinTone = 'porcelain' | 'fair' | 'tan' | 'brown' | 'deep';
export type AvatarHair = 'short' | 'long' | 'curly' | 'braids' | 'buzz' | 'ponytail';
export type AvatarHairColor = 'black' | 'brown' | 'blonde' | 'red' | 'blue';
export type AvatarOutfit = 'tunic' | 'robe' | 'armour' | 'explorer' | 'lab-coat';
export type AvatarAccessory = 'none' | 'glasses' | 'cape' | 'hat' | 'backpack';

export type AvatarConfig = {
  skinTone: AvatarSkinTone;
  hair: AvatarHair;
  hairColor: AvatarHairColor;
  outfit: AvatarOutfit;
  accessory: AvatarAccessory;
};

export type AvatarClassId = 'wizard' | 'knight' | 'ranger' | 'engineer' | 'scientist';

/**
 * A cosmetic the student has earned or bought, named by its drawing.
 *
 * Mirrors `ItemArt` in `@yuan-math/world` structurally rather than importing
 * it, exactly as `AvatarConfig` mirrors the learner's — this package depends on
 * nothing but React, and a drawing should not need to know what an inventory
 * is. `validateItems` keeps the two lists in step from the other side, by
 * refusing an item whose art no slot is drawn for.
 *
 * These are cosmetics and only cosmetics (docs/rpg.md §19). A staff is a
 * silhouette, never a capability: nothing anywhere reads equipment to change
 * difficulty, XP, recommendations or access.
 */
export type AvatarItemArt =
  | 'algebra-staff'
  | 'geometry-shield'
  | 'infinity-staff'
  | 'compass'
  | 'abacus'
  | 'gatekeeper-cape'
  | 'dragonscale-cape'
  | 'traveller-cape'
  | 'starlit-cape'
  | 'circlet'
  | 'scholar-cap'
  | 'sun-hat'
  | 'tide-hat'
  | 'keepers-lamp'
  | 'tally-cape'
  | 'north-parka'
  | 'sun-shawl';

/** What is worn where. Three slots, and an absent one is simply not drawn. */
export type AvatarEquipment = {
  held?: AvatarItemArt;
  cape?: AvatarItemArt;
  headgear?: AvatarItemArt;
};

/** Every value each option can take, in the order a picker should offer them. */
export const AVATAR_OPTIONS: { readonly [K in keyof AvatarConfig]: readonly AvatarConfig[K][] } = {
  skinTone: ['porcelain', 'fair', 'tan', 'brown', 'deep'],
  hair: ['short', 'long', 'curly', 'braids', 'buzz', 'ponytail'],
  hairColor: ['black', 'brown', 'blonde', 'red', 'blue'],
  outfit: ['tunic', 'robe', 'armour', 'explorer', 'lab-coat'],
  accessory: ['none', 'glasses', 'cape', 'hat', 'backpack'],
} as const;

/** The look a student who skipped character creation adventures in. */
export const DEFAULT_AVATAR: AvatarConfig = {
  skinTone: 'tan',
  hair: 'short',
  hairColor: 'black',
  outfit: 'tunic',
  accessory: 'none',
};

/*
 * Skin and hair are literal colours, like the Mascot's slate face — there is
 * no theme token for a person, and these must not flip in dark mode.
 */
const SKIN: Record<AvatarSkinTone, { fill: string; line: string }> = {
  porcelain: { fill: '#ffe4d0', line: '#d9a988' },
  fair: { fill: '#f1c9a5', line: '#cf9c72' },
  tan: { fill: '#d69a6b', line: '#b0784b' },
  brown: { fill: '#a76a3f', line: '#82502d' },
  deep: { fill: '#7c4a24', line: '#5d3418' },
};

const HAIR: Record<AvatarHairColor, string> = {
  black: '#2b2b33',
  brown: '#6b4a2f',
  blonde: '#e8c15a',
  red: '#c8502e',
  blue: '#4a7dd6',
};

/* Outfit fills carry no text, so the vivid `--X-face` weights are theirs. */
const OUTFIT: Record<AvatarOutfit, { fill: string; trim: string }> = {
  tunic: { fill: 'var(--grass-face)', trim: 'var(--grass-edge)' },
  robe: { fill: 'var(--grape-face)', trim: 'var(--grape-edge)' },
  armour: { fill: '#9aa5b1', trim: '#7b8794' },
  explorer: { fill: 'var(--gold-face)', trim: 'var(--gold-edge)' },
  'lab-coat': { fill: '#f4f4f2', trim: '#c9c9c4' },
};

const FACE_LINE = '#33323d';

/** The cap of hair every fringed style shares. */
function Fringe({ color }: { color: string }): JSX.Element {
  return <path d="M33 31 Q33 13 50 13 Q67 13 67 31 Q60 22 50 22 Q40 22 33 31" fill={color} />;
}

function HairBack({ hair, color }: { hair: AvatarHair; color: string }): JSX.Element | null {
  switch (hair) {
    case 'long':
      return <path d="M31 30 Q31 12 50 12 Q69 12 69 30 L69 52 Q69 58 63 58 L37 58 Q31 58 31 52 Z" fill={color} />;
    case 'braids':
      return (
        <g stroke={color} strokeWidth="7" strokeLinecap="round">
          <path d="M35 38 L33 56" fill="none" />
          <path d="M65 38 L67 56" fill="none" />
        </g>
      );
    case 'ponytail':
      return <path d="M64 22 Q80 26 77 50 Q75 58 70 51 Q74 36 60 26 Z" fill={color} />;
    default:
      return null;
  }
}

function HairFront({ hair, color }: { hair: AvatarHair; color: string }): JSX.Element | null {
  switch (hair) {
    case 'short':
    case 'long':
    case 'braids':
    case 'ponytail':
      return <Fringe color={color} />;
    case 'buzz':
      return <path d="M34 27 Q38 14 50 14 Q62 14 66 27 Q58 20 50 20 Q42 20 34 27" fill={color} />;
    case 'curly':
      return (
        <g fill={color}>
          <circle cx="36" cy="20" r="8" />
          <circle cx="46" cy="15" r="8" />
          <circle cx="56" cy="15" r="8" />
          <circle cx="64" cy="20" r="8" />
          <circle cx="32" cy="28" r="7" />
          <circle cx="68" cy="28" r="7" />
        </g>
      );
    default:
      return null;
  }
}

function Outfit({ outfit }: { outfit: AvatarOutfit }): JSX.Element {
  const { fill, trim } = OUTFIT[outfit];
  switch (outfit) {
    case 'robe':
      return (
        <g>
          <path d="M26 92 L34 54 Q40 48 50 48 Q60 48 66 54 L74 92 Z" fill={fill} />
          <path d="M28 84 L72 84" stroke={trim} strokeWidth="3" fill="none" />
        </g>
      );
    case 'armour':
      return (
        <g>
          <path d="M32 92 L32 62 Q32 50 44 48 L56 48 Q68 50 68 62 L68 92 Z" fill={fill} />
          <circle cx="33" cy="53" r="7" fill={trim} />
          <circle cx="67" cy="53" r="7" fill={trim} />
          <path d="M42 58 L58 58 L56 72 L44 72 Z" fill={trim} />
        </g>
      );
    case 'lab-coat':
      return (
        <g>
          <path d="M32 92 L32 62 Q32 50 44 48 L56 48 Q68 50 68 62 L68 92 Z" fill={fill} />
          <path d="M50 50 L50 92" stroke={trim} strokeWidth="2" fill="none" />
          <path d="M44 48 L50 58 L56 48" stroke={trim} strokeWidth="2.5" fill="none" />
        </g>
      );
    case 'explorer':
      return (
        <g>
          <path d="M32 92 L32 62 Q32 50 44 48 L56 48 Q68 50 68 62 L68 92 Z" fill={fill} />
          <path d="M36 50 L64 80" stroke="var(--sky-face)" strokeWidth="6" fill="none" />
          <rect x="32" y="74" width="36" height="5" fill={trim} />
        </g>
      );
    case 'tunic':
    default:
      return (
        <g>
          <path d="M32 92 L32 62 Q32 50 44 48 L56 48 Q68 50 68 62 L68 92 Z" fill={fill} />
          <rect x="32" y="74" width="36" height="5" fill={trim} />
        </g>
      );
  }
}

/** Each class is one held prop — a silhouette, never an ability. */
function ClassProp({ classId }: { classId: AvatarClassId }): JSX.Element {
  switch (classId) {
    case 'wizard':
      return (
        <g>
          <line x1="80" y1="92" x2="80" y2="42" stroke="#6b4a2f" strokeWidth="4" strokeLinecap="round" />
          <circle cx="80" cy="37" r="6" fill="var(--grape-face)" />
          <path d="M80 29 L82 33 L86 33 L83 36 L84 40 L80 38 L76 40 L77 36 L74 33 L78 33 Z" fill="var(--gold-face)" />
        </g>
      );
    case 'knight':
      return (
        <g>
          <path d="M14 58 L34 58 L34 74 Q34 84 24 88 Q14 84 14 74 Z" fill="var(--sky-face)" stroke="var(--sky-edge)" strokeWidth="3" />
          <path d="M24 62 L24 82 M17 70 L31 70" stroke="var(--sky-edge)" strokeWidth="3" fill="none" />
        </g>
      );
    case 'ranger':
      return (
        <g>
          <path d="M78 44 Q94 68 78 92" stroke="#6b4a2f" strokeWidth="4" fill="none" strokeLinecap="round" />
          <line x1="78" y1="44" x2="78" y2="92" stroke="#b0a284" strokeWidth="1.5" />
        </g>
      );
    case 'engineer':
      return (
        <g stroke="#7b8794" strokeWidth="5" strokeLinecap="round">
          <line x1="72" y1="86" x2="86" y2="60" />
          <path d="M83 52 A8 8 0 1 0 93 62" fill="none" />
        </g>
      );
    case 'scientist':
    default:
      return (
        <g>
          <rect x="77" y="60" width="6" height="10" fill="var(--sky-soft)" stroke="#7b8794" strokeWidth="2" />
          <path d="M77 70 L68 88 Q67 92 71 92 L89 92 Q93 92 92 88 L83 70 Z" fill="var(--sky-soft)" stroke="#7b8794" strokeWidth="2" />
          <path d="M73 80 L87 80 L91 89 Q91.5 91 89 91 L71 91 Q68.5 91 69 89 Z" fill="var(--grass-face)" />
        </g>
      );
  }
}

function Accessory({ accessory, position }: { accessory: AvatarAccessory; position: 'back' | 'front' }): JSX.Element | null {
  if (accessory === 'cape' && position === 'back') {
    return <path d="M33 50 L21 90 L36 83 L50 90 L64 83 L79 90 L67 50 Z" fill="var(--berry-face)" />;
  }
  if (accessory === 'backpack' && position === 'back') {
    return (
      <g fill="#8a6300">
        <rect x="22" y="54" width="9" height="22" rx="4" />
        <rect x="69" y="54" width="9" height="22" rx="4" />
      </g>
    );
  }
  if (position !== 'front') return null;
  if (accessory === 'glasses') {
    return (
      <g stroke="#3a3a44" strokeWidth="2.5" fill="none">
        <circle cx="43" cy="32" r="6" />
        <circle cx="57" cy="32" r="6" />
        <line x1="49" y1="32" x2="51" y2="32" />
      </g>
    );
  }
  if (accessory === 'hat') {
    return (
      <g>
        <path d="M37 18 Q37 7 50 7 Q63 7 63 18 Z" fill="var(--sky-face)" />
        <rect x="33" y="17" width="34" height="5" rx="2.5" fill="var(--sky-edge)" />
      </g>
    );
  }
  if (accessory === 'backpack') {
    return (
      <g stroke="#8a6300" strokeWidth="4" strokeLinecap="round">
        <line x1="38" y1="50" x2="42" y2="70" />
        <line x1="62" y1="50" x2="58" y2="70" />
      </g>
    );
  }
  return null;
}

/* -------------------------------------------------------------------------- */
/* Equipment layers                                                            */
/* -------------------------------------------------------------------------- */

/** The silhouette every cape shares, so a new one is a colour and a detail. */
const CAPE_PATH = 'M33 50 L21 90 L36 83 L50 90 L64 83 L79 90 L67 50 Z';

/** Worn behind the body, on the same layer as the accessory cape it replaces. */
function CapeLayer({ art }: { art: AvatarItemArt }): JSX.Element | null {
  switch (art) {
    case 'gatekeeper-cape':
      return (
        <g>
          <path d={CAPE_PATH} fill="var(--gold-face)" />
          <path d="M33 50 L67 50" stroke="var(--gold-edge)" strokeWidth="4" />
          <circle cx="50" cy="52" r="4" fill="var(--gold-deep)" />
        </g>
      );
    case 'dragonscale-cape':
      return (
        <g>
          <path d={CAPE_PATH} fill="var(--grass-face)" />
          {/* Scale rows, laid out from fixed numbers — no randomness below apps/web. */}
          <g stroke="var(--grass-edge)" strokeWidth="2" fill="none">
            <path d="M34 60 q5 6 10 0 q5 6 10 0 q5 6 10 0 q5 6 10 0" />
            <path d="M31 71 q6 6 12 0 q6 6 12 0 q6 6 12 0" />
          </g>
        </g>
      );
    case 'traveller-cape':
      return (
        <g>
          <path d={CAPE_PATH} fill="#8a6a45" />
          <path d="M33 50 L67 50" stroke="#6b5033" strokeWidth="5" />
        </g>
      );
    case 'starlit-cape':
      return (
        <g>
          <path d={CAPE_PATH} fill="var(--grape-face)" />
          {/* Nine stars at authored positions: the same picture on every render. */}
          <g fill="var(--gold-face)">
            {[
              [37, 60],
              [50, 56],
              [63, 61],
              [31, 74],
              [44, 70],
              [57, 72],
              [69, 76],
              [40, 82],
              [60, 84],
            ].map(([x, y]) => (
              <circle key={`${x}-${y}`} cx={x} cy={y} r="1.8" />
            ))}
          </g>
        </g>
      );
    default:
      return null;
  }
}

/** Worn over the hair, in front of the fringe and behind nothing. */
function HeadgearLayer({ art }: { art: AvatarItemArt }): JSX.Element | null {
  switch (art) {
    case 'circlet':
      return (
        <g>
          <path d="M34 24 Q50 30 66 24" stroke="var(--gold-face)" strokeWidth="4" fill="none" strokeLinecap="round" />
          <path d="M50 27 L53 22 L50 17 L47 22 Z" fill="var(--sky-face)" stroke="var(--gold-edge)" strokeWidth="1.5" />
        </g>
      );
    case 'scholar-cap':
      return (
        <g>
          <path d="M38 16 Q50 10 62 16 L62 21 Q50 26 38 21 Z" fill="#2f3540" />
          <path d="M28 14 L50 6 L72 14 L50 22 Z" fill="#3b424f" />
          <line x1="72" y1="14" x2="72" y2="26" stroke="var(--gold-face)" strokeWidth="2.5" />
          <circle cx="72" cy="27" r="3" fill="var(--gold-face)" />
        </g>
      );
    case 'sun-hat':
      return (
        <g>
          <ellipse cx="50" cy="21" rx="27" ry="7" fill="#e2c07a" />
          <path d="M39 20 Q39 8 50 8 Q61 8 61 20 Z" fill="#efd39a" />
          <path d="M38 19 Q50 24 62 19" stroke="var(--berry-face)" strokeWidth="3" fill="none" />
        </g>
      );
    case 'tide-hat':
      /* A sun hat that has been out on the sandbar: the brim has dropped and
         the band is salt-bleached rather than berry. */
      return (
        <g>
          <ellipse cx="50" cy="22" rx="28" ry="8" fill="#cdb489" />
          <path d="M40 21 Q40 9 50 9 Q60 9 60 21 Z" fill="#e4d3b0" />
          <path d="M39 20 Q50 25 61 20" stroke="var(--sky-face)" strokeWidth="3" fill="none" />
        </g>
      );
    case 'sun-shawl':
      /* Loose and pale, thrown rather than fastened: the drape is the drawing,
         which is what tells it apart from the parka's heavy square shoulders. */
      return (
        <g>
          <path d="M33 32 Q50 27 67 32 Q72 52 66 70 Q50 76 34 70 Q28 52 33 32 Z" fill="#e6d7b4" />
          <path d="M33 32 Q50 27 67 32 Q60 40 50 39 Q40 40 33 32 Z" fill="#f2e7cd" />
          <path d="M38 68 Q50 73 62 68" stroke="#c9b489" strokeWidth="2.5" fill="none" strokeLinecap="round" />
        </g>
      );
    case 'north-parka':
      /* Hooded and fur-trimmed: the trim is the drawing, because a plain heavy
         cape is every other cape on the map in a colder colour. */
      return (
        <g>
          <path d="M31 33 Q50 28 69 33 L73 73 Q50 79 27 73 Z" fill="#4a5f7a" />
          <path d="M31 33 Q50 28 69 33 L70 40 Q50 35 30 40 Z" fill="#5f7794" />
          <path
            d="M30 36 Q50 30 70 36"
            stroke="#e8e4dc"
            strokeWidth="5"
            fill="none"
            strokeLinecap="round"
          />
          <path d="M36 72 Q50 76 64 72" stroke="#e8e4dc" strokeWidth="3.5" fill="none" strokeLinecap="round" />
        </g>
      );
    case 'tally-cape':
      /* Marks on the hem rather than a ledger — five of them, and they are the
         drawing rather than an ornament on it. */
      return (
        <g>
          <path d="M32 34 Q50 30 68 34 L72 72 Q50 78 28 72 Z" fill="#7d8aa0" />
          <path d="M32 34 Q50 30 68 34 L69 44 Q50 40 31 44 Z" fill="#96a3b8" />
          <g stroke="var(--gold-face)" strokeWidth="2" strokeLinecap="round">
            <line x1="40" y1="62" x2="40" y2="70" />
            <line x1="45" y1="62" x2="45" y2="70" />
            <line x1="50" y1="62" x2="50" y2="70" />
            <line x1="55" y1="62" x2="55" y2="70" />
            <line x1="38" y1="70" x2="57" y2="62" />
          </g>
        </g>
      );
    default:
      return null;
  }
}

/**
 * Held in the hands, in place of the class prop.
 *
 * Replacing rather than stacking: a wizard holding a staff *and* an abacus is
 * two things in one pair of hands. The class is still the default (docs/rpg.md
 * §10 — a costume), and an equipped item is the student's later choice about
 * the same slot.
 */
function HeldLayer({ art }: { art: AvatarItemArt }): JSX.Element | null {
  switch (art) {
    case 'algebra-staff':
      return (
        <g>
          <line x1="80" y1="92" x2="80" y2="42" stroke="#6b4a2f" strokeWidth="4" strokeLinecap="round" />
          <circle cx="80" cy="35" r="8" fill="var(--sky-face)" stroke="var(--sky-edge)" strokeWidth="2" />
          {/* The balance the flavour line means: two arms, level. */}
          <path d="M73 35 L87 35 M80 29 L80 41" stroke="var(--sky-ink)" strokeWidth="2.5" strokeLinecap="round" />
        </g>
      );
    case 'geometry-shield':
      return (
        <g>
          <path
            d="M14 56 L34 56 L34 74 Q34 85 24 89 Q14 85 14 74 Z"
            fill="var(--grass-face)"
            stroke="var(--grass-edge)"
            strokeWidth="3"
          />
          <path d="M17 78 L24 62 L31 78 Z" fill="none" stroke="var(--grass-deep)" strokeWidth="2.5" />
          <path d="M17 78 L31 78" stroke="var(--grass-deep)" strokeWidth="2.5" />
        </g>
      );
    case 'infinity-staff':
      return (
        <g>
          <line x1="80" y1="92" x2="80" y2="44" stroke="#4a3f5c" strokeWidth="4" strokeLinecap="round" />
          <g fill="none" stroke="var(--grape-face)" strokeWidth="3.5">
            <circle cx="75" cy="36" r="5" />
            <circle cx="85" cy="36" r="5" />
          </g>
        </g>
      );
    case 'compass':
      return (
        <g>
          <circle cx="80" cy="66" r="10" fill="var(--gold-face)" stroke="var(--gold-edge)" strokeWidth="2.5" />
          <circle cx="80" cy="66" r="6" fill="var(--paper-raised)" />
          <path d="M80 61 L82 66 L80 71 L78 66 Z" fill="var(--berry-face)" />
        </g>
      );
    case 'keepers-lamp':
      /* A storm lantern, carried by the ring. The flame is the only warm thing
         on the rock, so it is the one saturated colour in the drawing. */
      return (
        <g>
          <path d="M80 52 Q80 47 84 47 Q88 47 88 52" stroke="var(--rule-strong)" strokeWidth="2" fill="none" />
          <rect x="76" y="52" width="16" height="5" rx="2" fill="#6b7280" />
          <rect x="77" y="57" width="14" height="16" rx="2" fill="var(--paper-raised)" stroke="#6b7280" strokeWidth="2" />
          <path d="M84 62 Q87 66 84 70 Q81 66 84 62 Z" fill="var(--gold-face)" />
          <rect x="76" y="73" width="16" height="4" rx="2" fill="#6b7280" />
        </g>
      );
    case 'abacus':
      return (
        <g>
          <rect x="70" y="56" width="22" height="20" rx="3" fill="none" stroke="#6b4a2f" strokeWidth="3" />
          <g stroke="#6b4a2f" strokeWidth="1.5">
            <line x1="70" y1="63" x2="92" y2="63" />
            <line x1="70" y1="69" x2="92" y2="69" />
          </g>
          <g fill="var(--berry-face)">
            <circle cx="75" cy="63" r="2.4" />
            <circle cx="81" cy="63" r="2.4" />
            <circle cx="88" cy="69" r="2.4" />
            <circle cx="82" cy="69" r="2.4" />
          </g>
        </g>
      );
    default:
      return null;
  }
}

export function Avatar({
  config = DEFAULT_AVATAR,
  classId,
  equipment,
  size = 96,
  className = '',
  label,
}: {
  config?: AvatarConfig;
  /** Undefined is a student who has not chosen — a plain adventurer. */
  classId?: AvatarClassId;
  /**
   * Cosmetics the student is wearing. Absent, or absent per slot, draws exactly
   * what an unequipped adventurer has always looked like — this is additive,
   * and every existing caller keeps its picture.
   */
  equipment?: AvatarEquipment;
  size?: number;
  className?: string;
  /**
   * The name a screen reader should read the drawing out as, when the page
   * cannot supply one beside it.
   *
   * Absent — the default, and what every caller before this had — the avatar is
   * decorative and hidden, because the name and the class are usually written
   * next to it and hearing them twice is worse than not hearing the picture.
   * Supplied, it becomes an image with that name: on a page where the drawing
   * *is* the subject, silence is the wrong answer. Named by the page rather
   * than assembled here, because "your adventurer" and "a preview of the
   * character you are making" are the same drawing in two different sentences.
   */
  label?: string;
}): JSX.Element {
  const skin = SKIN[config.skinTone];
  const hairColor = HAIR[config.hairColor];

  /*
   * An equipped piece wins its slot outright, and the creation-time accessory
   * standing in the same place steps aside. Two capes on one back, or a circlet
   * inside a sun hat, is not a richer look — it is a bug that renders.
   */
  const accessory: AvatarAccessory =
    (equipment?.cape && config.accessory === 'cape') || (equipment?.headgear && config.accessory === 'hat')
      ? 'none'
      : config.accessory;

  return (
    <div
      className={`avatar ${className}`}
      style={{ width: size, height: size }}
      {...(label === undefined ? { 'aria-hidden': true } : { role: 'img', 'aria-label': label })}
    >
      <svg viewBox="0 0 100 100" width={size} height={size} role="presentation" focusable="false">
        {/*
          `data-item` names the layer in the markup. It is what a test asserts a
          round-trip on, and what tells you which of twelve near-identical
          `<g>`s you are looking at in a devtools tree — the SVG itself is a
          pile of anonymous paths.
        */}
        {equipment?.cape ? (
          <g data-item={equipment.cape}>
            <CapeLayer art={equipment.cape} />
          </g>
        ) : null}
        <Accessory accessory={accessory} position="back" />
        <HairBack hair={config.hair} color={hairColor} />

        {/* hands, tucked behind the torso so a sleeve overlaps them */}
        <circle cx="27" cy="68" r="5" fill={skin.fill} />
        <circle cx="73" cy="68" r="5" fill={skin.fill} />

        <Outfit outfit={config.outfit} />

        {/* head */}
        <circle cx="50" cy="32" r="17" fill={skin.fill} stroke={skin.line} strokeWidth="2" />

        {/* face */}
        <circle cx="43" cy="32" r="2.5" fill={FACE_LINE} />
        <circle cx="57" cy="32" r="2.5" fill={FACE_LINE} />
        <path d="M44 39 q6 5 12 0" stroke={FACE_LINE} strokeWidth="2.5" fill="none" strokeLinecap="round" />

        <HairFront hair={config.hair} color={hairColor} />
        <Accessory accessory={accessory} position="front" />
        {equipment?.headgear ? (
          <g data-item={equipment.headgear}>
            <HeadgearLayer art={equipment.headgear} />
          </g>
        ) : null}
        {equipment?.held ? (
          <g data-item={equipment.held}>
            <HeldLayer art={equipment.held} />
          </g>
        ) : classId !== undefined ? (
          <ClassProp classId={classId} />
        ) : null}
      </svg>
    </div>
  );
}
